import { ref, reactive } from '@vue/composition-api'
import reportsApi from '@/services/api/reports'

function buildFilterParams(filters) {
  const params = {}
  if (filters.year != null) params.year = filters.year
  if (filters.draft_id != null) params.draft_id = filters.draft_id
  if (filters.commit) params.commit = filters.commit
  if (filters.search) params.search = filters.search
  return params
}

export default function useReportsIncoming() {
  const items = ref([])
  const totals = ref([])
  const loading = ref(false)
  const currentPage = ref(1)
  const lastPage = ref(1)
  const total = ref(0)

  const filters = reactive({
    year: null,
    draft_id: null,
    commit: null,
    search: '',
    sort: 'date',
    direction: 'desc'
  })

  async function fetchTotals() {
    try {
      const params = {}
      if (filters.year != null) params.year = filters.year
      if (filters.draft_id != null) params.draft_id = filters.draft_id
      const res = await reportsApi.getIncomingTotals(params)
      const data = res.data.data != null ? res.data.data : {}
      totals.value = Object.keys(data).map(key => ({
        label: key,
        amount: parseFloat(data[key] || 0).toFixed(2)
      }))
    } catch (_) {
      totals.value = []
    }
  }

  async function fetch() {
    loading.value = true
    try {
      const params = {
        ...buildFilterParams(filters),
        page: currentPage.value
      }
      if (filters.sort) {
        params.sort = filters.sort
        params.direction = filters.direction
      }
      const res = await reportsApi.getIncoming(params)
      const list = res.data.data != null ? res.data.data : []
      items.value = list.map(r => ({
        id: r.id,
        date: r.created_at,
        invoiceNumber: r.invoice_number || '',
        pdfUrl: r.pdf_url || null,
        competitorName: r.user != null ? `${r.user.first_name} ${r.user.last_name}` : '',
        paymentType: r.commit != null ? r.commit : '',
        amount: r.amount
      }))
      const meta = res.data.meta != null ? res.data.meta : {}
      currentPage.value = meta.current_page != null ? meta.current_page : 1
      lastPage.value = meta.last_page != null ? meta.last_page : 1
      total.value = meta.total != null ? meta.total : 0
    } catch (_) { // handled by http interceptor
    } finally {
      loading.value = false
    }
  }

  function setCommit(commit) {
    filters.commit = commit
    currentPage.value = 1
    fetch()
  }

  function setSearch(val) {
    filters.search = val
    currentPage.value = 1
    fetch()
  }

  function resetSearch() {
    filters.search = ''
    currentPage.value = 1
    fetch()
  }

  function setSort(field) {
    if (filters.sort === field) {
      filters.direction = filters.direction === 'asc' ? 'desc' : 'asc'
    } else {
      filters.sort = field
      filters.direction = 'asc'
    }
    fetch()
  }

  function setPage(page) {
    currentPage.value = page
    fetch()
  }

  async function updateAmount(reportId, amount) {
    try {
      const res = await reportsApi.updateIncomingAmount(reportId, { amount })
      await fetch()
      await fetchTotals()
      return { success: true, message: res.data.message != null ? res.data.message : 'Amount updated.' }
    } catch (err) {
      const resp = err.response && err.response.data ? err.response.data : {}
      return {
        success: false,
        message: resp.message != null ? resp.message : 'An error occurred.',
        code: resp.code
      }
    }
  }

  const exportingFormat = ref(null)

  async function exportReport(format) {
    if (exportingFormat.value != null) return
    exportingFormat.value = format
    try {
      const params = { ...buildFilterParams(filters), format }
      if (filters.sort) {
        params.sort = filters.sort
        params.direction = filters.direction
      }
      const res = await reportsApi.exportIncoming(params)

      if (format === 'pdf') {
        const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
        window.open(blobUrl, '_blank')
        return
      }

      const disposition = res.headers['content-disposition']
      let filename = `reports-incoming.${format}`
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/)
        if (match) filename = match[1]
      }
      const url = URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (_) { // handled by http interceptor
    } finally {
      exportingFormat.value = null
    }
  }

  const generatingPdfIds = ref(new Set())

  async function generateReportPdf(reportId) {
    if (generatingPdfIds.value.has(reportId)) return
    generatingPdfIds.value = new Set([...generatingPdfIds.value, reportId])
    try {
      const res = await reportsApi.getIncomingPdf(reportId)
      const url = res.data.data.url
      if (!url) return
      window.open(url, '_blank')
      const item = items.value.find(i => i.id === reportId)
      if (item) item.pdfUrl = url
    } catch (_) { // handled by http interceptor
    } finally {
      const next = new Set(generatingPdfIds.value)
      next.delete(reportId)
      generatingPdfIds.value = next
    }
  }

  return {
    items,
    totals,
    loading,
    filters,
    currentPage,
    lastPage,
    total,
    fetch,
    fetchTotals,
    setCommit,
    setSearch,
    resetSearch,
    setSort,
    setPage,
    updateAmount,
    exportReport,
    exportingFormat,
    generateReportPdf,
    generatingPdfIds
  }
}



// WEBPACK FOOTER //
// ./src/composables/reports/useReportsIncoming.js